import React, { useMemo, useState } from "react"
import { Plus, TrashIcon } from "lucide-react"

import useSavedPaymentMethods from "~/hooks/useSavedPaymentMethods"
import LoadingWrapper from "~/library/LoadingWrapper"

import type SavedPaymentMethod from "~/types/SavedPaymentMethod"
import ToggleChevron from "~/components/ui/ToggleChevron"
type PayableType = "booking" | "sponsorship" | "deal"

interface Props {
  payableType: PayableType
  payableId?: string
  title?: string
  onAddCard?: () => void
  onSetDefault?: (method: SavedPaymentMethod) => void
  onDelete?: (method: SavedPaymentMethod) => Promise<void> | void
}

const formatCardLabel = (method: SavedPaymentMethod) => {
  return (
    method.displayName ||
    [method.brand, method.last4 ? `•••• ${method.last4}` : null].filter(Boolean).join(" ")
  )
}

const formatExpiry = (method: SavedPaymentMethod) => {
  if (!method.expMonth || !method.expYear) return "—"
  return `${String(method.expMonth).padStart(2, "0")}/${method.expYear}`
}

const DefaultCardRow = ({
  method,
  onAddCard,
  onDelete,
}: {
  method: SavedPaymentMethod
  onAddCard?: () => void
  onDelete?: (method: SavedPaymentMethod) => Promise<void> | void
}) => {
  return (
    <tr>
      <td>
        <div className="fw-semibold">{formatCardLabel(method)}</div>
        <div className="text-muted small">
          {method.brand || "Card"} {method.last4 ? `•••• ${method.last4}` : ""}
        </div>
      </td>

      <td className="text-nowrap">{formatExpiry(method)}</td>

      <td>
        <span className="badge bg-success">Default</span>
      </td>

      <td className="text-end">
        <div className="d-flex justify-content-end gap-3 align-items-center flex-wrap">
          {onAddCard && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onAddCard}>
              Replace Card
            </button>
          )}

          {onDelete && (
            <TrashIcon
              size={18}
              className="text-danger cursor-pointer"
              onClick={() => onDelete(method)}
            />
          )}
        </div>
      </td>
    </tr>
  )
}

const OtherCardRow = ({
  method,
  onSetDefault,
  onDelete,
}: {
  method: SavedPaymentMethod
  onSetDefault?: (method: SavedPaymentMethod) => void
  onDelete?: (method: SavedPaymentMethod) => Promise<void> | void
}) => {
  return (
    <tr>
      <td>
        <div className="fw-semibold">{formatCardLabel(method)}</div>
        <div className="text-muted small">
          {method.brand || "Card"} {method.last4 ? `•••• ${method.last4}` : ""}
        </div>
      </td>

      <td className="text-nowrap">{formatExpiry(method)}</td>

      <td>
        {method.linkedToCurrentPayable ? (
          <span className="text-muted small">Used for this record</span>
        ) : (
          <span className="text-muted small">Saved</span>
        )}
      </td>

      <td className="text-end">
        <div className="d-flex justify-content-end gap-3 align-items-center flex-wrap">
          {onSetDefault && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onSetDefault(method)}
            >
              Set Default
            </button>
          )}

          {onDelete && (
            <TrashIcon
              size={18}
              className="text-danger cursor-pointer"
              onClick={() => onDelete(method)}
            />
          )}
        </div>
      </td>
    </tr>
  )
}

const SavedPaymentMethodsCard: React.FC<Props> = ({
  payableType,
  payableId,
  title = "Saved Payment Methods",
  onAddCard,
  onSetDefault,
  onDelete,
}) => {
  const { savedPaymentMethods, isLoading, error } = useSavedPaymentMethods(payableType, payableId)
  const [showOtherCards, setShowOtherCards] = useState(false)

  const { defaultMethod, otherMethods } = useMemo(() => {
    const methods = savedPaymentMethods || []
    const selectedDefault = methods.find((method) => method.isDefault) || methods[0] || null
    const remaining = selectedDefault
      ? methods.filter((method) => method.id !== selectedDefault.id)
      : []

    return {
      defaultMethod: selectedDefault,
      otherMethods: remaining,
    }
  }, [savedPaymentMethods])

  if (!payableId) {
    return <div className="text-muted">Payment methods unavailable.</div>
  }

  return (
    <LoadingWrapper isLoading={isLoading}>
      {error ? (
        <div className="text-danger">Failed to load saved cards.</div>
      ) : (
        <>
          <div className="d-flex align-items-center mb-3">
            <h4 className="card-title mb-0">{title}</h4>

            {onAddCard && (
              <button type="button" className="btn btn-primary ms-auto" onClick={onAddCard}>
                <Plus className="me-2" />
                Add Card
              </button>
            )}
          </div>

          <div className="table-responsive">
            <table className="table table-centered table-nowrap mb-0">
              <thead>
                <tr className="table-light">
                  <th>Card</th>
                  <th>Expiry</th>
                  <th>Status</th>
                  <th className="text-end">
                    <div className="d-flex justify-content-end align-items-center gap-3">
                      <span>Actions</span>
                      {otherMethods.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-link p-0 d-inline-flex align-items-center"
                          onClick={() => setShowOtherCards((prev) => !prev)}
                          title={showOtherCards ? "Hide additional cards" : "Show additional cards"}
                          style={{ opacity: 0.8 }}
                          onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.8")}
                        >
                          <ToggleChevron open={showOtherCards} />
                        </button>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {!defaultMethod ? (
                  <tr>
                    <td colSpan={4} className="text-muted py-3">
                      No saved cards on file.
                    </td>
                  </tr>
                ) : (
                  <>
                    <DefaultCardRow
                      method={defaultMethod}
                      onAddCard={onAddCard}
                      onDelete={onDelete}
                    />

                    {showOtherCards &&
                      otherMethods.map((method) => (
                        <OtherCardRow
                          key={method.id}
                          method={method}
                          onSetDefault={onSetDefault}
                          onDelete={onDelete}
                        />
                      ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </LoadingWrapper>
  )
}

export default SavedPaymentMethodsCard
